import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import InfographicHero from "@/components/InfographicHero";
import {
  ArrowRight, Rocket, ShieldCheck, Users, Sparkles, HardHat, FlaskConical,
  Globe, ExternalLink,
} from "lucide-react";

/**
 * ClientsHub — dual-purpose portfolio landing:
 *   1. Served at `clients.websitetech.pro/` (root of the client subdomain)
 *   2. Served at `websitetech.pro/clients` (portfolio section of the main site)
 *
 * Uses `isClientsSubdomain` to decide whether to render the WebsiteTech
 * Navbar/Footer shell. On the clients subdomain it hides them so the hub
 * feels like a dedicated portfolio site. On the main site it keeps them.
 */

const CLIENT_SITES = [
  {
    slug: "qst",
    name: "Quality Safety Times",
    kicker: "QST · Construction training",
    tagline: "Reduce rework. Prevent injuries. Build better.",
    body: "Expert-authored, trade-specific quality and safety training platform for construction crews. Field-ready lessons in the foreman's language.",
    accent: "amber",
    icon: HardHat,
    launched: "2026",
  },
  {
    slug: "litlab",
    name: "L.I.T. Labs",
    kicker: "LitLab · Metallurgical testing",
    tagline: "Aerospace-grade testing. Documented to survive any audit.",
    body: "Nadcap + AC7006 (ISO/IEC 17025) accredited metallurgical laboratory delivering same-week quotes and chain-of-custody audit-ready reports since 1985.",
    accent: "sky",
    icon: FlaskConical,
    launched: "2026",
  },
];

const ONBOARDING_SITES = [
  {
    slug: "commerce",
    name: "AI Commerce Intelligence",
    kicker: "In onboarding · pilot phase",
    tagline: "Multi-store price intelligence + automated publishing.",
    body: "Retail chain pilot syncing prices across 12 storefronts with automated publishing rules. Currently onboarding — public rollout coming.",
    accent: "emerald",
    icon: Sparkles,
    launched: "Q4 2026",
  },
];

const ACCENT_MAP = {
  amber: {
    ring: "hover:border-amber-300",
    dot: "bg-amber-500",
    tag: "bg-amber-50 text-amber-800 border-amber-200",
    arrow: "text-amber-700",
    grad: "from-amber-50 via-white to-orange-50",
  },
  sky: {
    ring: "hover:border-sky-300",
    dot: "bg-sky-500",
    tag: "bg-sky-50 text-sky-800 border-sky-200",
    arrow: "text-sky-700",
    grad: "from-sky-50 via-white to-cyan-50",
  },
  emerald: {
    ring: "hover:border-emerald-300",
    dot: "bg-emerald-500",
    tag: "bg-emerald-50 text-emerald-800 border-emerald-200",
    arrow: "text-emerald-700",
    grad: "from-emerald-50 via-white to-teal-50",
  },
};

const SiteCard = ({ site }) => {
  const a = ACCENT_MAP[site.accent] || ACCENT_MAP.sky;
  const Icon = site.icon;
  return (
    <Link
      to={`/${site.slug}`}
      className={`group relative block overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br ${a.grad} p-6 transition ${a.ring} hover:shadow-xl`}
      data-testid={`clients-card-${site.slug}`}
    >
      <div className="flex items-center gap-2">
        <span className={`h-2 w-2 rounded-full ${a.dot}`} />
        <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
          {site.kicker}
        </div>
      </div>
      <div className="mt-4 flex items-start gap-3">
        <div className="rounded-xl border border-slate-200 bg-white p-2.5 shadow-sm">
          <Icon className="h-6 w-6 text-slate-700" />
        </div>
        <div className="min-w-0">
          <div className="font-heading text-2xl font-black text-slate-900">
            {site.name}
          </div>
          <div className="mt-0.5 text-sm font-semibold text-slate-700">
            {site.tagline}
          </div>
        </div>
      </div>
      <p className="mt-4 text-sm leading-relaxed text-slate-600">{site.body}</p>
      <div className="mt-5 flex items-center justify-between">
        <Badge variant="outline" className={`text-[10px] uppercase ${a.tag}`}>
          Live {site.launched}
        </Badge>
        <span className={`inline-flex items-center gap-1 text-sm font-semibold ${a.arrow} transition group-hover:gap-2`}>
          Open site <ArrowRight className="h-4 w-4" />
        </span>
      </div>
    </Link>
  );
};

const OnboardingCard = ({ site }) => {
  const a = ACCENT_MAP[site.accent] || ACCENT_MAP.emerald;
  const Icon = site.icon;
  return (
    <div
      className={`relative overflow-hidden rounded-2xl border border-dashed border-slate-300 bg-white p-6 transition hover:border-slate-400`}
      data-testid={`clients-onboarding-${site.slug}`}
    >
      <div className="flex items-center gap-2">
        <span className={`h-2 w-2 animate-pulse rounded-full ${a.dot}`} />
        <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
          {site.kicker}
        </div>
      </div>
      <div className="mt-4 flex items-start gap-3">
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-2.5">
          <Icon className="h-6 w-6 text-slate-700" />
        </div>
        <div>
          <div className="font-heading text-xl font-bold text-slate-900">
            {site.name}
          </div>
          <div className="text-sm text-slate-600">{site.tagline}</div>
        </div>
      </div>
      <p className="mt-4 text-sm text-slate-600">{site.body}</p>
      <Badge variant="outline" className={`mt-4 text-[10px] uppercase ${a.tag}`}>
        {site.launched}
      </Badge>
    </div>
  );
};

export default function ClientsHub({ isClientsSubdomain }) {
  useEffect(() => {
    // Canonicalise: on the main site, /clients canonical to itself.
    // On the clients subdomain, canonical to the subdomain root.
    const c = document.createElement("link");
    c.rel = "canonical";
    c.href = isClientsSubdomain
      ? "https://clients.websitetech.pro/"
      : "https://websitetech.pro/clients";
    document.head.appendChild(c);
    return () => { try { document.head.removeChild(c); } catch { /* ignore */ } };
  }, [isClientsSubdomain]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50" data-testid="clients-hub">
      {!isClientsSubdomain && <Navbar />}

      {/* Hero */}
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:py-20">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-orange-600">
            <Globe className="h-4 w-4" />
            {isClientsSubdomain ? "clients.websitetech.pro" : "WebsiteTech · Portfolio"}
          </div>
          <h1 className="mt-4 font-heading text-4xl font-black leading-tight text-slate-900 sm:text-5xl lg:text-6xl">
            Live client sites <span className="text-orange-600">built and operated</span> by WebsiteTech.
          </h1>
          <p className="mt-6 max-w-2xl text-base text-slate-600 sm:text-lg">
            Every brand below is a real business we've launched — its own domain,
            its own voice, its own audience. Explore them, or see who's
            onboarding next.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/si"
              className="inline-flex items-center gap-2 rounded-full bg-orange-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-orange-700"
              data-testid="clients-cta-si"
            >
              <Rocket className="h-4 w-4" /> Run a Search Intelligence audit
            </Link>
            <Link
              to="/growth-operator"
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-900 transition hover:bg-slate-50"
              data-testid="clients-cta-growth"
            >
              <Users className="h-4 w-4" /> Meet the Growth Operator
            </Link>
          </div>
          <div className="mt-10">
            <InfographicHero slug="complete-platform" hint={false} />
          </div>
        </div>
      </section>

      {/* Client Sites */}
      <section className="mx-auto max-w-6xl px-4 py-12">
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-5 w-5 text-emerald-600" />
          <h2 className="font-heading text-2xl font-black text-slate-900 sm:text-3xl">
            Client Sites
          </h2>
          <span className="text-sm text-slate-500">· {CLIENT_SITES.length} live</span>
        </div>
        <p className="mt-2 max-w-2xl text-sm text-slate-600">
          Fully-launched brands operating on their own. Tap any card to open the
          live site.
        </p>
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          {CLIENT_SITES.map((s) => <SiteCard key={s.slug} site={s} />)}
        </div>
      </section>

      {/* Onboarding Sites */}
      <section className="mx-auto max-w-6xl px-4 pb-16">
        <div className="flex items-center gap-3">
          <Sparkles className="h-5 w-5 text-amber-600" />
          <h2 className="font-heading text-2xl font-black text-slate-900 sm:text-3xl">
            Onboarding Sites
          </h2>
          <span className="text-sm text-slate-500">· preparing for launch</span>
        </div>
        <p className="mt-2 max-w-2xl text-sm text-slate-600">
          Brands currently being built or in pilot. Watch this space.
        </p>
        <div className="mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {ONBOARDING_SITES.map((s) => <OnboardingCard key={s.slug} site={s} />)}
        </div>
      </section>

      {/* Footer band */}
      {isClientsSubdomain ? (
        <section className="border-t border-slate-200 bg-slate-50">
          <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-4 py-8 text-sm text-slate-500 sm:flex-row">
            <span>© {new Date().getFullYear()} · Client portfolio hub</span>
            <a
              href="https://websitetech.pro"
              className="inline-flex items-center gap-1 font-semibold text-slate-700 hover:text-orange-600"
              data-testid="clients-madeby-footer"
            >
              Made by <span className="text-orange-600">WebsiteTech.pro</span>
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </section>
      ) : (
        <Footer />
      )}
    </div>
  );
}
